/**
 * FloatingChatButton Component
 *
 * A floating action button (FAB) that stays fixed in the bottom-right corner
 * of the screen, allowing users to invoke AI chat at any time.
 *
 * Only appears when the user is actively interacting with a problem
 * (clicking or focusing within a ProblemCard).
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AIChatIcon } from './CustomIcons';
import { ChatSidePanel } from './ChatSidePanel';
import { ChatModal } from './ChatModal';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useActiveProblem } from '../contexts/ActiveProblemContext';
import { useAuthModal } from '../hooks/useAuthModal';
import { AuthRequiredModal } from './AuthRequiredModal';
import { LoginModal } from './LoginModal';
import { savePendingAction } from '../utils/authRedirectState';
import { FEATURES } from '../config/features';
import './FloatingChatButton.css';

export const FloatingChatButton: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const activeProblemContext = useActiveProblem();
  const activeProblem = activeProblemContext?.activeProblem;

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isDiscovered, setIsDiscovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose: handleAuthClose,
  } = useAuthModal();

  // Check if AI assistant has been discovered this session
  useEffect(() => {
    const discovered = sessionStorage.getItem('aiAssistantDiscovered') === 'true';
    setIsDiscovered(discovered);
  }, []);

  // Animate visibility when active problem changes
  useEffect(() => {
    if (activeProblem) {
      // Small delay for smooth appearance
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [activeProblem]);

  // Listen for auth redirect completion to resume pending actions
  useEffect(() => {
    if (!activeProblem) return;

    const handleAuthRedirect = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = (customEvent.detail || {}) as { pendingAction?: any };
      if (pendingAction?.type === 'open_chat' && pendingAction.data.problemId === activeProblem.id) {
        setIsChatOpen(true);
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
  }, [activeProblem]);

  // Don't render if no active problem
  if (!activeProblem) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark AI assistant as discovered for this session
    if (!isDiscovered) {
      sessionStorage.setItem('aiAssistantDiscovered', 'true');
      setIsDiscovered(true);
    }

    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'open_chat',
        data: { problemId: activeProblem.id },
      });
      requireAuth(() => {});
      return;
    }

    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const button = (
    <button
      onClick={handleClick}
      className={`floating-chat-button ${isDiscovered ? 'discovered' : ''} ${isVisible ? 'visible' : ''}`}
      title={`Chat with AI about "${activeProblem.title}"`}
      aria-label={`Open AI chat for "${activeProblem.title}"`}
    >
      <AIChatIcon size={24} color="white" className="floating-chat-icon" />
    </button>
  );

  return (
    <>
      {/* Render button via portal to document.body for fixed positioning */}
      {/* Hide the button when chat is already open */}
      {!isChatOpen && ReactDOM.createPortal(button, document.body)}

      {/* Chat Interface */}
      {FEATURES.USE_SIDE_PANEL_CHAT ? (
        <ChatSidePanel
          isOpen={isChatOpen}
          onClose={handleCloseChat}
          problemId={activeProblem.id}
          problemTitle={activeProblem.title}
        />
      ) : (
        <ChatModal
          isOpen={isChatOpen}
          onClose={handleCloseChat}
          problemId={activeProblem.id}
          problemTitle={activeProblem.title}
        />
      )}

      {/* Auth Modals */}
      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleAuthClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleAuthClose}
        onSuccess={handleLoginSuccess}
      />
    </>
  );
};

export default FloatingChatButton;
